import type { UserDateFormat, UserTimeFormat } from './auth.models';

export const TEMPLATE_FIELD_TYPES = [
  'signature',
  'initials',
  'textbox',
  'date_signed',
  'checkbox',
  'radio',
  'name',
  'email',
  'title',
  'company',
  'editable_date',
  'image',
  'dropdown',
] as const;

export type TemplateFieldType = (typeof TEMPLATE_FIELD_TYPES)[number];

export type TemplateFieldFontFamily =
  | 'Helvetica'
  | 'Courier'
  | 'Times New Roman'
  | 'Noto Sans'
  | 'Carlito';
export type TemplateFieldTextAlign = 'left' | 'center' | 'right';
export type TemplateFieldTabIndexMode = 'auto' | 'custom';
export type TemplateTextboxResizeType =
  | 'grow_horizontally'
  | 'grow_vertically'
  | 'grow_both'
  | 'fixed'
  | 'auto_resize_font';
export type TemplateTextboxValidationType = 'none' | 'email' | 'number' | 'minimum' | 'maximum' | 'regex';
export type TemplateOptionValidationType = 'none' | 'minimum' | 'maximum' | 'exact';
export type TemplateDateFormat = UserDateFormat;
export type TemplateTimeFormat = 'None' | UserTimeFormat;

export interface TemplateFieldRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TemplateFieldSenderActions {
  allowEditing: boolean;
  allowDeletion: boolean;
  requireDefaultValue: boolean;
}

export interface TemplateFieldCommonSettings {
  senderActions: TemplateFieldSenderActions;
  dataSyncTag?: string;
  tabIndexMode: TemplateFieldTabIndexMode;
  tabIndex?: number;
  autoPositioningEnabled: boolean;
}

export interface TemplateFieldFontSettings {
  family: TemplateFieldFontFamily;
  size: number;
  lineHeight: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  align: TemplateFieldTextAlign;
  rtl: boolean;
  characterSpacing: number;
}

export interface TemplateTextboxSettings {
  readOnly: boolean;
  maskValue: boolean;
  defaultText?: string;
  hintText?: string;
  resizeType: TemplateTextboxResizeType;
  fixedWidth: boolean;
  characterLimit?: number;
  validationType: TemplateTextboxValidationType;
  validationValue?: string;
}

export interface TemplateDropdownSettings {
  hintText?: string;
  options: string[];
  defaultOption?: string;
}

export interface TemplateCheckboxSettings {
  optionGroupId?: string;
  value?: string;
  checked?: boolean;
  size: number;
  values?: string[];
  defaultValues?: string[];
  validationType?: TemplateOptionValidationType;
  validationCount?: number;
}

export interface TemplateRadioSettings {
  groupName?: string;
  value?: string;
  checked?: boolean;
  size: number;
  values?: string[];
  defaultValue?: string;
  validationType?: TemplateOptionValidationType;
  validationCount?: number;
}

export interface TemplateDateSettings {
  dateFormat: TemplateDateFormat;
  timeFormat: TemplateTimeFormat;
  defaultDate?: string;
}

export interface TemplateImageSettings {
  title?: string;
  instruction?: string;
  acceptJpgJpeg: boolean;
  acceptSvg: boolean;
  acceptPng: boolean;
  acceptBmp: boolean;
}

export interface TemplateFieldSettings {
  common: TemplateFieldCommonSettings;
  font: TemplateFieldFontSettings;
  textbox: TemplateTextboxSettings;
  dropdown: TemplateDropdownSettings;
  checkbox: TemplateCheckboxSettings;
  radio: TemplateRadioSettings;
  date: TemplateDateSettings;
  image: TemplateImageSettings;
}

export interface TemplateField {
  id: string;
  fieldKey: string;
  type: TemplateFieldType;
  role: string;
  page: number;
  rect: TemplateFieldRect;
  required: boolean;
  label?: string;
  placeholder?: string;
  settings: TemplateFieldSettings;
}

export interface TemplateRole {
  id: string;
  name: string;
  defaultRecipientName?: string;
  defaultRecipientEmail?: string;
  order: number;
}

export interface TemplateDocumentDetails {
  title: string;
  message?: string;
}

export interface TemplateUsageSettings {
  allowAddMoreFiles: boolean;
  allowReplaceRearrangeDeleteFiles: boolean;
  allowAddMoreRecipients: boolean;
  allowEditDocumentMessage: boolean;
  allowAddNewFields: boolean;
  allowEditOrDeleteFields: boolean;
}

export interface TemplateDocumentSettings {
  brandId?: string;
  brandName?: string;
  brand?: string;
  allowPrintAndSign: boolean;
}

export interface UploadedPdfFile {
  fileId: string;
  s3Key: string;
  bucket: string;
  originalName: string;
  sizeBytes: number;
  pageCount: number;
  uploadedAt: string;
}

export interface TemplatePayload {
  name: string;
  description?: string;
  sourceFile: UploadedPdfFile;
  fields: TemplateField[];
  signingOrderEnabled: boolean;
  roles: TemplateRole[];
  ccEmails: string[];
  document: TemplateDocumentDetails;
  tags: string[];
  usageSettings: TemplateUsageSettings;
  documentSettings: TemplateDocumentSettings;
}

export type TemplateStatus = 'draft' | 'created';

export interface TemplateSummary extends TemplatePayload {
  _id: string;
  status: TemplateStatus;
  ownerUserId?: string;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateListResponse {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  items: TemplateSummary[];
}

export interface PublicTemplateConfigSessionResponse {
  template: {
    id: string;
    status: TemplateStatus;
    name: string;
    description?: string;
    sourceFile: {
      fileId: string;
      originalName: string;
      pageCount: number;
    };
    signingOrderEnabled: boolean;
    ccEmails: string[];
    document: TemplateDocumentDetails;
    tags: string[];
    usageSettings: TemplateUsageSettings;
    documentSettings: TemplateDocumentSettings;
  };
  roles: TemplateRole[];
  fields: TemplateField[];
  pdfUrl: string;
}

export interface PublicTemplateConfigSaveResponse {
  templateId: string;
  fields: TemplateField[];
  updatedAt: string;
}

export interface PublicTemplateConfigUrlResponse {
  templateId: string;
  configureFieldsUrl: string;
}
